import type { Annotation } from "@rowan/prisma";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { trpc } from "../../../utils/trpc";
import { ArticleContext } from "../WithArticle/ArticleContext";
import { useQuery } from "../../hooks/useQuery";
import { AnnotationContext } from "./AnnotationContext";

export const ArticleAnnotationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const didTapMenuRef = useRef(false);
  const query = useQuery();
  const annotationId = query.get("annotationId");
  const { articleSlug } = useContext(ArticleContext);
  const [annotationsOpen, setAnnotationsOpen] = useState(true);
  const [focusedId, setFocusedId] = useState<string | undefined>(
    annotationId || undefined,
  );

  const { data: annotations, isLoading } = trpc.getAnnotations.useQuery(
    { articleSlug },
    {
      enabled: !!articleSlug,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      cacheTime: 0,
    },
  );

  const updateFocusedId = useCallback((id: string | undefined) => {
    setFocusedId(id);
    const { pathname, search, hash } = window.location;
    const params = new URLSearchParams(search);

    if (id) {
      params.set("annotationId", id);
    } else {
      params.delete("annotationId");
    }

    const newUrl = `${pathname}?${params.toString()}${hash}`;
    window.history.replaceState(null, "", newUrl);
  }, []);

  useEffect(() => {
    if (!isLoading && annotations?.length === 0) {
      setAnnotationsOpen(false);
    }
  }, [annotations, isLoading]);

  const context = useMemo(() => {
    return {
      annotations: annotations?.map((annotation) => ({
        ...annotation,
        lastModified: new Date(annotation.lastModified),
        createdAt: new Date(annotation.createdAt),
      })) as Annotation[],
      annotationsOpen,
      setAnnotationsOpen,
      focusedId,
      setFocusedId: updateFocusedId,
      didTapMenuRef,
    };
  }, [annotations, annotationsOpen, focusedId, updateFocusedId]);

  return (
    <AnnotationContext.Provider value={context}>
      {children}
    </AnnotationContext.Provider>
  );
};
